import React, { useState, useEffect, useRef, useMemo } from 'react';
import { format } from 'date-fns';
import {
  BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  ResponsiveContainer, LineChart, Line
} from 'recharts';
import {
  TrendingUp, CheckCircle, Clock, AlertCircle, Activity,
  Ticket, Timer, Layers, BarChart3, ArrowRight,
  ExternalLink
} from 'lucide-react';
import ServerHealthCard from './ServerHealthCard';

const formatDuration = (ms) => {
  if (!ms && ms !== 0) return '-';
  if (ms < 1000) return '< 1 menit';
  const seconds = Math.floor(ms / 1000);
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);
  const parts = [];
  if (days > 0) parts.push(days + ' hari');
  if (hours % 24 > 0) parts.push((hours % 24) + ' jam');
  if (minutes % 60 > 0) parts.push((minutes % 60) + ' menit');
  if (parts.length === 0) return '< 1 menit';
  return parts.join(' ');
};

const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    return (
      <div className="bg-white border border-gray-200 rounded-xl shadow-lg p-3">
        <p className="text-sm font-semibold text-gray-900 mb-1">{label}</p>
        {payload.map((entry, index) => (
          <p key={index} className="text-sm" style={{ color: entry.color }}>
            {entry.name}: <span className="font-bold">{entry.value}</span>
          </p>
        ))}
      </div>
    );
  }
  return null;
};

// ─── Circular Progress Ring (with animated counter) ────────
const CircularProgress = ({ percentage, size = 80, strokeWidth = 6, color = '#10B981', duration = 1000 }) => {
  const [animatedValue, setAnimatedValue] = useState(0);
  const [completed, setCompleted] = useState(false);
  const rafRef = useRef(null);

  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference - (animatedValue / 100) * circumference;

  useEffect(() => {
    setCompleted(false);
    setAnimatedValue(0);
    const startTime = performance.now();
    const from = 0;
    const to = percentage;

    const animate = (now) => {
      const elapsed = now - startTime;
      const progress = Math.min(elapsed / duration, 1);
      // Ease-out cubic
      const eased = 1 - Math.pow(1 - progress, 3);
      const current = Math.round(from + (to - from) * eased);
      setAnimatedValue(current);

      if (progress < 1) {
        rafRef.current = requestAnimationFrame(animate);
      } else {
        setCompleted(true);
      }
    };

    rafRef.current = requestAnimationFrame(animate);
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      setCompleted(false);
    };
  }, [percentage, duration]);

  return (
    <div className={`relative inline-flex items-center justify-center ${completed ? 'animate-glow-pulse' : ''}`} style={{ color }}>
      {/* Glow ring behind (only when completed) */}
      {completed && (
        <svg
          width={size + 12}
          height={size + 12}
          className="absolute animate-glow-ring"
          style={{ opacity: 0.4, color }}
        >
          <circle
            cx={(size + 12) / 2}
            cy={(size + 12) / 2}
            r={radius + 4}
            fill="none"
            stroke={color}
            strokeWidth={strokeWidth}
            strokeLinecap="round"
            strokeDasharray={circumference}
            strokeDashoffset={offset}
            style={{
              filter: `blur(3px)`,
              transition: 'none',
            }}
          />
        </svg>
      )}
      <svg width={size} height={size} className="transform -rotate-90 relative z-10">
        {/* Background ring */}
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke="#e5e7eb"
          strokeWidth={strokeWidth}
        />
        {/* Progress ring */}
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke={color}
          strokeWidth={strokeWidth}
          strokeLinecap="round"
          strokeDasharray={circumference}
          strokeDashoffset={offset}
          style={{ transition: 'none' }}
        />
      </svg>
      <span className="absolute text-sm font-bold tabular-nums z-10" style={{ color }}>
        {animatedValue}%
      </span>
    </div>
  );
};

const DashboardPage = ({ activities, stats, tickets, ticketStats, ticketOpenCount, onNavigate }) => {
  // ─── Activity Stats ─────────────────────────────────────────
  const totalInfo = stats?.total || {};
  const activityStats = {
    total: totalInfo.total || activities.length,
    completed: totalInfo.completed || activities.filter(a => a.status === 'Completed').length,
    inProgress: totalInfo.inProgress || activities.filter(a => a.status === 'In Progress').length,
    pending: totalInfo.pending || activities.filter(a => a.status === 'Pending').length,
  };

  const completionRate = activityStats.total > 0
    ? Math.round((activityStats.completed / activityStats.total) * 100)
    : 0;

  const avgActualDuration = useMemo(() => {
    const completed = activities.filter(a => a.status === 'Completed' && a.actualDuration);
    return completed.length > 0
      ? completed.reduce((sum, a) => sum + a.actualDuration, 0) / completed.length
      : 0;
  }, [activities]);

  // ─── Ticket Stats ───────────────────────────────────────────
  const tStats = ticketStats?.total || { total: 0, open: 0, inProgress: 0, resolved: 0, closed: 0, urgent: 0 };

  const resolutionRate = tStats.total > 0
    ? Math.round((tStats.resolved / tStats.total) * 100)
    : 0;

  // ─── Chart Data ────────────────────────────────────────────
  const categoryData = useMemo(() => {
    if (stats?.categories?.length > 0) return stats.categories.map(c => ({ name: c.name, value: c.count }));
    const counts = {};
    activities.forEach(a => { counts[a.category] = (counts[a.category] || 0) + 1; });
    return Object.entries(counts).map(([name, value]) => ({ name, value }));
  }, [activities, stats]);

  const statusData = useMemo(() => {
    if (stats?.statuses?.length > 0) return stats.statuses.map(s => ({ name: s.name, value: s.count }));
    const counts = {};
    activities.forEach(a => { counts[a.status] = (counts[a.status] || 0) + 1; });
    return Object.entries(counts).map(([name, value]) => ({ name, value }));
  }, [activities, stats]);

  const priorityData = useMemo(() => {
    if (stats?.priorities?.length > 0) return stats.priorities.map(p => ({ name: p.name, value: p.count }));
    const counts = {};
    activities.forEach(a => { counts[a.priority] = (counts[a.priority] || 0) + 1; });
    return Object.entries(counts).map(([name, value]) => ({ name, value }));
  }, [activities, stats]);

  const monthlyData = useMemo(() => {
    if (stats?.monthly?.length > 0) {
      return stats.monthly.map(m => ({ month: m.month, activities: m.count }));
    }
    const counts = {};
    activities.forEach(a => {
      const month = format(new Date(a.date), 'MMM yyyy');
      counts[month] = (counts[month] || 0) + 1;
    });
    return Object.entries(counts)
      .map(([month, activities]) => ({ month, activities }))
      .sort((a, b) => new Date(a.month) - new Date(b.month));
  }, [activities, stats]);

  // ─── Recent Data ───────────────────────────────────────────
  const recentActivities = [...activities]
    .sort((a, b) => new Date(b.date) - new Date(a.date))
    .slice(0, 5);

  const recentTickets = (ticketStats?.recent || (tickets || []).slice(0, 4)).filter(Boolean);

  // ─── Colors ────────────────────────────────────────────────
  const COLORS = ['#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#EC4899'];
  const STATUS_COLORS = ['#6B7280', '#F59E0B', '#10B981'];
  const PRIORITY_COLORS = ['#3B82F6', '#F59E0B', '#EF4444'];

  const getRateColor = (rate) => {
    if (rate < 50) return {
      hex: '#EF4444',
      gradient: 'from-red-600 to-red-400',
      iconColor: 'text-red-600',
      text: 'text-red-600',
      bgLight: 'bg-red-50',
      bg: 'bg-red-50',
      iconBg: 'bg-red-100',
      barGradient: 'from-red-500 via-rose-400 to-red-500',
      shadowClass: 'shadow-red-200',
    };
    if (rate < 80) return {
      hex: '#F59E0B',
      gradient: 'from-yellow-500 to-orange-400',
      iconColor: 'text-yellow-600',
      text: 'text-yellow-600',
      bgLight: 'bg-yellow-50',
      bg: 'bg-yellow-50',
      iconBg: 'bg-yellow-100',
      barGradient: 'from-yellow-500 via-orange-400 to-yellow-500',
      shadowClass: 'shadow-yellow-200',
    };
    return {
      hex: '#10B981',
      gradient: 'from-green-600 to-emerald-400',
      iconColor: 'text-green-600',
      text: 'text-green-600',
      bgLight: 'bg-green-50',
      bg: 'bg-green-50',
      iconBg: 'bg-green-100',
      barGradient: 'from-green-500 via-emerald-400 to-green-500',
      shadowClass: 'shadow-green-200',
    };
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Completed': return 'bg-green-100 text-green-800 border-green-200';
      case 'In Progress': return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'Pending': return 'bg-gray-100 text-gray-800 border-gray-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getTicketStatusColor = (status) => {
    switch (status) {
      case 'Open': return 'bg-red-100 text-red-700 border-red-200';
      case 'In Progress': return 'bg-yellow-100 text-yellow-700 border-yellow-200';
      case 'Resolved': return 'bg-green-100 text-green-700 border-green-200';
      case 'Closed': return 'bg-gray-100 text-gray-600 border-gray-200';
      default: return 'bg-gray-100 text-gray-600 border-gray-200';
    }
  };

  const getInitials = (title) => {
    return title.split(' ').map(w => w[0]).join('').substring(0, 2).toUpperCase();
  };

  // ─── Navigation shortcuts ────────────────────────────────
  const goTo = (view) => {
    if (onNavigate) onNavigate(view);
  };

  // ─── Summary Cards (with click navigation) ────────────────
  const summaryCards = [
    {
      title: 'Total Activities',
      value: activityStats.total,
      icon: Layers,
      gradient: 'from-blue-600 to-blue-400',
      bgLight: 'bg-blue-50',
      iconBg: 'bg-blue-100',
      iconColor: 'text-blue-600',
      subtitle: 'All activities',
      onClick: () => goTo('list'),
    },
    {
      title: 'Completed',
      value: activityStats.completed,
      icon: CheckCircle,
      ...getRateColor(completionRate),
      subtitle: `${completionRate}% completion rate`,
      progress: completionRate,
      prominent: true,
      onClick: () => goTo('stats'),
    },
    {
      title: 'In Progress',
      value: activityStats.inProgress,
      icon: Clock,
      gradient: 'from-yellow-500 to-orange-400',
      bgLight: 'bg-yellow-50',
      iconBg: 'bg-yellow-100',
      iconColor: 'text-yellow-600',
      subtitle: 'Currently active',
      onClick: () => goTo('list'),
    },
    {
      title: 'Pending',
      value: activityStats.pending,
      icon: AlertCircle,
      gradient: 'from-red-500 to-rose-400',
      bgLight: 'bg-red-50',
      iconBg: 'bg-red-100',
      iconColor: 'text-red-600',
      subtitle: 'Awaiting action',
      onClick: () => goTo('list'),
    },
    {
      title: 'Rata-rata Durasi',
      value: avgActualDuration > 0 ? formatDuration(avgActualDuration) : '-',
      icon: Timer,
      gradient: 'from-purple-600 to-purple-400',
      bgLight: 'bg-purple-50',
      iconBg: 'bg-purple-100',
      iconColor: 'text-purple-600',
      subtitle: 'Avg completion time',
      onClick: () => goTo('stats'),
    },
  ];

  const ticketSummaryCards = [
    {
      title: 'Total Tickets',
      value: tStats.total,
      icon: Ticket,
      gradient: 'from-indigo-600 to-indigo-400',
      bgLight: 'bg-indigo-50',
      iconColor: 'text-indigo-600',
      onClick: () => goTo('tickets'),
    },
    {
      title: 'Open',
      value: tStats.open,
      icon: AlertCircle,
      gradient: 'from-red-500 to-rose-400',
      bgLight: 'bg-red-50',
      iconColor: 'text-red-600',
      badge: ticketOpenCount > 0 ? `${ticketOpenCount} need attention` : null,
      onClick: () => goTo('tickets'),
    },
    {
      title: 'In Progress',
      value: tStats.inProgress,
      icon: Clock,
      gradient: 'from-yellow-500 to-orange-400',
      bgLight: 'bg-yellow-50',
      iconColor: 'text-yellow-600',
      onClick: () => goTo('tickets'),
    },
    {
      title: 'Resolved',
      value: tStats.resolved,
      icon: CheckCircle,
      ...getRateColor(resolutionRate),
      subtitle: `${resolutionRate}% resolution rate`,
      progress: resolutionRate,
      promptPercentage: true,
      onClick: () => goTo('report-tickets'),
    },
  ];

  return (
    <div className="space-y-6 animate-fade-in">
      {/* ─── Page Header ───────────────────────────────────── */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Dashboard &amp; Statistic</h2>
          <p className="mt-1 text-sm text-gray-500">
            Comprehensive overview of your activity system
          </p>
        </div>
        <div className="flex items-center space-x-3">
          <div className="flex items-center space-x-2 bg-blue-50 rounded-lg px-3 py-2">
            <Activity className="w-4 h-4 text-blue-600" />
            <span className="text-sm font-medium text-blue-700">{activityStats.total} activities</span>
          </div>
          <div className="flex items-center space-x-2 bg-indigo-50 rounded-lg px-3 py-2">
            <Ticket className="w-4 h-4 text-indigo-600" />
            <span className="text-sm font-medium text-indigo-700">{tStats.total} tickets</span>
          </div>
        </div>
      </div>

      {/* ─── Server Health ──────────────────────────────────── */}
      <ServerHealthCard />

      {/* ─── Activity Summary Cards ────────────────────────── */}
      <div>
        <div className="flex items-center space-x-2 mb-4">
          <div className="w-1 h-6 rounded-full bg-blue-500" />
          <h3 className="text-lg font-semibold text-gray-900">Activity Overview</h3>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
          {summaryCards.map((card, index) => (
            <div
              key={card.title}
              className="card animate-slide-up overflow-hidden relative group cursor-pointer transition-all duration-200 hover:shadow-xl hover:-translate-y-1"
              style={{ animationDelay: `${index * 50}ms` }}
              onClick={card.onClick}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); card.onClick?.(); } }}
            >
              {/* Decorative gradient bar at top */}
              <div className={`h-1 w-full bg-gradient-to-r ${card.gradient}`} />
              
              {/* Decorative dot pattern overlay */}
              <div className="absolute inset-0 opacity-[0.03]" style={{
                backgroundImage: `radial-gradient(circle at 8px 8px, currentColor 1px, transparent 1px)`,
                backgroundSize: '16px 16px',
                color: card.iconColor.replace('text-', '').includes('green') ? '#059669' :
                        card.iconColor.replace('text-', '').includes('blue') ? '#2563eb' :
                        card.iconColor.replace('text-', '').includes('yellow') ? '#d97706' :
                        card.iconColor.replace('text-', '').includes('red') ? '#dc2626' :
                        card.iconColor.replace('text-', '').includes('purple') ? '#9333ea' : '#374151'
              }} />

              <div className="p-4">
                {/* Arrow indicator on hover */}
                <div className="absolute top-3 right-3 opacity-0 group-hover:opacity-100 transition-all duration-200 translate-x-1 group-hover:translate-x-0">
                  <div className={`p-1 rounded-full ${card.bgLight}`}>
                    <ArrowRight className={`w-3.5 h-3.5 ${card.iconColor}`} />
                  </div>
                </div>
                <div className="flex items-start justify-between">
                  <div className={`relative p-2.5 rounded-xl ${card.bgLight} group-hover:scale-110 transition-transform duration-200`}>
                    {/* Icon glow */}
                    <div className={`absolute inset-0 rounded-xl ${card.bgLight} blur-md opacity-0 group-hover:opacity-60 transition-opacity duration-300`} />
                    <card.icon className={`relative w-5 h-5 ${card.iconColor}`} />
                  </div>
                  <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full border ${
                    index === 0 ? 'bg-blue-50 text-blue-700 border-blue-200' :
                    index === 1 ? 'bg-green-50 text-green-700 border-green-200' :
                    index === 2 ? 'bg-yellow-50 text-yellow-700 border-yellow-200' :
                    index === 3 ? 'bg-red-50 text-red-700 border-red-200' :
                    'bg-purple-50 text-purple-700 border-purple-200'
                  }`}>
                    {card.subtitle}
                  </span>
                </div>
                <div className="mt-3">
                  <p className="text-xs font-medium text-gray-500 uppercase tracking-wider">{card.title}</p>
                  <p className="mt-1 text-4xl font-extrabold text-gray-900 tracking-tight">{card.value}</p>
                  {card.prominent && card.subtitle && (
                    <div className="mt-2 flex items-center gap-3">
                      <CircularProgress percentage={card.progress} size={48} strokeWidth={5} color={card.hex} />
                      <div className="flex-1 min-w-0">
                        <p className={`text-sm font-bold ${card.iconColor}`}>
                          {card.subtitle}
                        </p>
                        <p className="text-[11px] text-gray-400 mt-0.5 truncate">{card.value} of {activityStats.total} completed</p>
                      </div>
                    </div>
                  )}
                </div>
                {card.progress !== undefined && activityStats.total > 0 && (
                  <div className="mt-3">
                    <div className="w-full bg-gray-100 rounded-full h-2.5 overflow-hidden ring-1 ring-inset ring-gray-200/50">
                      <div
                        className={`bg-gradient-to-r ${card.barGradient} h-2.5 rounded-full transition-all duration-1000 shadow-lg ${card.shadowClass}`}
                        style={{ width: `${card.progress}%` }}
                      />
                    </div>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* ─── Ticket Summary Cards ──────────────────────────── */}
      <div>
        <div className="flex items-center space-x-2 mb-4">
          <div className="w-1 h-6 rounded-full bg-indigo-500" />
          <h3 className="text-lg font-semibold text-gray-900">Ticket Overview</h3>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {ticketSummaryCards.map((card, index) => (
            <div
              key={card.title}
              className="card animate-slide-up overflow-hidden relative group cursor-pointer transition-all duration-200 hover:shadow-xl hover:-translate-y-1"
              style={{ animationDelay: `${index * 50}ms` }}
              onClick={card.onClick}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); card.onClick?.(); } }}
            >
              {/* Decorative gradient bar at top */}
              <div className={`h-1 w-full bg-gradient-to-r ${card.gradient}`} />
              
              {/* Decorative dot pattern overlay */}
              <div className="absolute inset-0 opacity-[0.03]" style={{
                backgroundImage: `radial-gradient(circle at 8px 8px, currentColor 1px, transparent 1px)`,
                backgroundSize: '16px 16px',
                color: card.iconColor?.replace('text-', '').includes('indigo') ? '#4f46e5' :
                        card.iconColor?.replace('text-', '').includes('red') ? '#dc2626' :
                        card.iconColor?.replace('text-', '').includes('yellow') ? '#d97706' :
                        card.iconColor?.replace('text-', '').includes('green') ? '#059669' : '#374151'
              }} />

              <div className="p-4">
                {/* Arrow indicator on hover */}
                <div className="absolute top-3 right-3 opacity-0 group-hover:opacity-100 transition-all duration-200 translate-x-1 group-hover:translate-x-0">
                  <div className={`p-1 rounded-full ${card.bgLight}`}>
                    <ArrowRight className={`w-3.5 h-3.5 ${card.iconColor}`} />
                  </div>
                </div>
                <div className="flex items-start justify-between">
                  <div className={`relative p-2.5 rounded-xl ${card.bgLight} group-hover:scale-110 transition-transform duration-200`}>
                    <div className={`absolute inset-0 rounded-xl ${card.bgLight} blur-md opacity-0 group-hover:opacity-60 transition-opacity duration-300`} />
                    <card.icon className={`relative w-5 h-5 ${card.iconColor}`} />
                  </div>
                  {card.badge && (
                    <span className="text-[10px] font-medium px-2 py-0.5 rounded-full bg-red-50 text-red-700 border border-red-200">
                      {card.badge}
                    </span>
                  )}
                </div>
                <div className="mt-3">
                  <p className="text-xs font-medium text-gray-500 uppercase tracking-wider">{card.title}</p>
                  <p className="mt-1 text-4xl font-extrabold text-gray-900 tracking-tight">{card.value}</p>
                  {card.subtitle && (
                    <p className={`mt-1 ${card.promptPercentage ? 'text-base' : 'text-xs'} font-semibold text-gray-500`}>
                      {card.subtitle}
                    </p>
                  )}
                </div>
                {card.progress !== undefined && tStats.total > 0 && card.promptPercentage && (
                  <div className="mt-3 flex items-center justify-between">
                    <CircularProgress percentage={card.progress} size={60} strokeWidth={5} color={card.hex} />
                    <div className="flex-1 ml-3">
                      <div className="w-full bg-gray-100 rounded-full h-1.5 overflow-hidden ring-1 ring-inset ring-gray-200/50">
                        <div
                          className={`bg-gradient-to-r ${card.barGradient} h-1.5 rounded-full transition-all duration-1000 shadow-lg ${card.shadowClass}`}
                          style={{ width: `${card.progress}%` }}
                        />
                      </div>
                      <p className="text-[10px] text-gray-400 mt-1">{card.value} resolved of {tStats.total}</p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* ─── Urgent Alert ──────────────────────────────────── */}
      {tStats.urgent > 0 && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-4 animate-slide-up">
          <div className="flex items-center space-x-2">
            <AlertCircle className="w-5 h-5 text-red-500 animate-pulse" />
            <p className="text-sm font-semibold text-red-800">
              {tStats.urgent} urgent ticket{tStats.urgent > 1 ? 's' : ''} need{tStats.urgent === 1 ? 's' : ''} immediate attention!
            </p>
          </div>
        </div>
      )}

      {/* ─── Charts Grid ────────────────────────────────────── */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Category Distribution (Pie) */}
        <div className="card overflow-hidden relative">
          <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-bl from-blue-100/40 to-transparent rounded-bl-full" />
          <div className="card-header relative z-10">
            <div className="flex items-center space-x-2">
              <div className="w-2 h-2 rounded-full bg-blue-500 animate-pulse" />
              <h3 className="section-title">Activities by Category</h3>
            </div>
          </div>
          <div className="card-body relative z-10">
            {categoryData.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={categoryData}
                    cx="50%" cy="50%"
                    labelLine={false}
                    label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                    outerRadius={90} innerRadius={30}
                    dataKey="value"
                  >
                    {categoryData.map((_, i) => (
                      <Cell key={i} fill={COLORS[i % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip content={<CustomTooltip />} />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <div className="text-center py-12">
                <svg className="inline-block w-20 h-20 text-gray-200 mb-4" viewBox="0 0 100 100" fill="none">
                  <circle cx="50" cy="50" r="40" stroke="currentColor" strokeWidth="3" strokeDasharray="8 4" />
                  <circle cx="50" cy="50" r="25" stroke="currentColor" strokeWidth="3" strokeDasharray="6 4" />
                  <circle cx="50" cy="50" r="10" stroke="currentColor" strokeWidth="3" strokeDasharray="4 4" />
                  <line x1="50" y1="10" x2="50" y2="90" stroke="currentColor" strokeWidth="1.5" strokeDasharray="4 4" />
                  <line x1="10" y1="50" x2="90" y2="50" stroke="currentColor" strokeWidth="1.5" strokeDasharray="4 4" />
                </svg>
                <p className="text-gray-400 font-medium">Belum ada data kategori</p>
                <p className="text-gray-300 text-sm mt-1">Tambah aktivitas untuk melihat statistik</p>
              </div>
            )}
          </div>
        </div>

        {/* Status Distribution (Bar) */}
        <div className="card overflow-hidden relative">
          <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-bl from-green-100/40 to-transparent rounded-bl-full" />
          <div className="card-header relative z-10">
            <div className="flex items-center space-x-2">
              <div className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />
              <h3 className="section-title">Activities by Status</h3>
            </div>
          </div>
          <div className="card-body relative z-10">
            {statusData.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={statusData} barSize={60}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis dataKey="name" tick={{ fontSize: 13 }} />
                  <YAxis tick={{ fontSize: 13 }} />
                  <Tooltip content={<CustomTooltip />} />
                  <Bar dataKey="value" radius={[6, 6, 0, 0]}>
                    {statusData.map((_, i) => (
                      <Cell key={i} fill={STATUS_COLORS[i % STATUS_COLORS.length]} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="text-center py-12">
                <svg className="inline-block w-20 h-20 text-gray-200 mb-4" viewBox="0 0 100 100" fill="none">
                  <rect x="15" y="50" width="16" height="40" rx="3" stroke="currentColor" strokeWidth="3" />
                  <rect x="42" y="30" width="16" height="60" rx="3" stroke="currentColor" strokeWidth="3" />
                  <rect x="69" y="40" width="16" height="50" rx="3" stroke="currentColor" strokeWidth="3" />
                  <line x1="10" y1="25" x2="90" y2="25" stroke="currentColor" strokeWidth="1.5" strokeDasharray="4 4" />
                </svg>
                <p className="text-gray-400 font-medium">Belum ada data status</p>
                <p className="text-gray-300 text-sm mt-1">Tambah aktivitas untuk melihat statistik</p>
              </div>
            )}
          </div>
        </div>

        {/* Priority Distribution */}
        <div className="card overflow-hidden relative">
          <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-bl from-yellow-100/40 to-transparent rounded-bl-full" />
          <div className="card-header relative z-10">
            <div className="flex items-center space-x-2">
              <div className="w-2 h-2 rounded-full bg-yellow-500 animate-pulse" />
              <h3 className="section-title">Activities by Priority</h3>
            </div>
          </div>
          <div className="card-body relative z-10">
            {priorityData.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={priorityData} barSize={60}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis dataKey="name" tick={{ fontSize: 13 }} />
                  <YAxis tick={{ fontSize: 13 }} />
                  <Tooltip content={<CustomTooltip />} />
                  <Bar dataKey="value" radius={[6, 6, 0, 0]}>
                    {priorityData.map((_, i) => (
                      <Cell key={i} fill={PRIORITY_COLORS[i % PRIORITY_COLORS.length]} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="text-center py-12">
                <svg className="inline-block w-20 h-20 text-gray-200 mb-4" viewBox="0 0 100 100" fill="none">
                  <polygon points="50,10 60,40 90,40 65,60 75,90 50,70 25,90 35,60 10,40 40,40" stroke="currentColor" strokeWidth="3" />
                  <line x1="50" y1="10" x2="50" y2="90" stroke="currentColor" strokeWidth="1.5" strokeDasharray="4 4" opacity="0.5" />
                  <line x1="10" y1="50" x2="90" y2="50" stroke="currentColor" strokeWidth="1.5" strokeDasharray="4 4" opacity="0.5" />
                </svg>
                <p className="text-gray-400 font-medium">Belum ada data prioritas</p>
                <p className="text-gray-300 text-sm mt-1">Tambah aktivitas untuk melihat statistik</p>
              </div>
            )}
          </div>
        </div>

        {/* Monthly Trend */}
        <div className="card overflow-hidden relative">
          <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-bl from-purple-100/40 to-transparent rounded-bl-full" />
          <div className="card-header relative z-10">
            <div className="flex items-center space-x-2">
              <div className="w-2 h-2 rounded-full bg-purple-500 animate-pulse" />
              <h3 className="section-title">Monthly Trend</h3>
            </div>
          </div>
          <div className="card-body relative z-10">
            {monthlyData.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={monthlyData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis dataKey="month" tick={{ fontSize: 12 }} />
                  <YAxis tick={{ fontSize: 13 }} />
                  <Tooltip content={<CustomTooltip />} />
                  <Legend />
                  <Line
                    type="monotone" dataKey="activities"
                    stroke="#3B82F6" strokeWidth={3}
                    dot={{ fill: '#3B82F6', strokeWidth: 2, r: 5 }}
                    activeDot={{ r: 7, strokeWidth: 2 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            ) : (
              <div className="text-center py-12">
                <svg className="inline-block w-20 h-20 text-gray-200 mb-4" viewBox="0 0 100 100" fill="none">
                  <polyline points="10,70 25,60 40,65 55,40 70,50 85,30 90,35" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
                  <circle cx="25" cy="60" r="4" stroke="currentColor" strokeWidth="2" />
                  <circle cx="55" cy="40" r="4" stroke="currentColor" strokeWidth="2" />
                  <circle cx="85" cy="30" r="4" stroke="currentColor" strokeWidth="2" />
                  <line x1="10" y1="20" x2="90" y2="20" stroke="currentColor" strokeWidth="1.5" strokeDasharray="4 4" />
                </svg>
                <p className="text-gray-400 font-medium">Belum ada data bulanan</p>
                <p className="text-gray-300 text-sm mt-1">Tambah aktivitas untuk melihat statistik</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ─── Recent Data Grid ──────────────────────────────── */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Recent Activities */}
        <div className="card">
          <div className="card-header flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <Activity className="w-5 h-5 text-blue-500" />
              <h3 className="section-title">Recent Activities</h3>
            </div>
            <div className="flex items-center space-x-2">
              <span className="bg-blue-50 text-blue-700 text-xs font-medium px-2.5 py-1 rounded-full">
                Latest {recentActivities.length}
              </span>
              <button
                onClick={() => goTo('list')}
                className="text-xs font-semibold text-blue-600 hover:text-blue-700 flex items-center space-x-1 transition-colors"
              >
                <span>View All</span>
                <ArrowRight className="w-3 h-3" />
              </button>
            </div>
          </div>
          <div className="card-body relative">
            {recentActivities.length > 0 ? (
              <div className="relative">
                {/* Timeline vertical line */}
                <div className="absolute left-5 top-2 bottom-2 w-0.5 bg-gradient-to-b from-blue-200 via-blue-100 to-transparent rounded-full" />
                <div className="space-y-1">
                  {recentActivities.map((activity, i) => (
                    <div
                      key={activity._id || activity.id}
                      onClick={() => goTo('list')}
                      className="flex items-start space-x-3 p-3 rounded-lg hover:bg-blue-50/70 transition-all duration-200 cursor-pointer group relative"
                      role="button"
                      tabIndex={0}
                      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); goTo('list'); } }}
                    >
                      {/* Timeline dot */}
                      <div className={`absolute left-[18px] top-[22px] w-1.5 h-1.5 rounded-full ring-2 ring-white z-10 transition-transform duration-200 group-hover:scale-150 ${
                        activity.status === 'Completed' ? 'bg-green-500' :
                        activity.status === 'In Progress' ? 'bg-yellow-500' : 'bg-gray-400'
                      }`} />
                      <div className={`flex-shrink-0 w-10 h-10 rounded-xl flex items-center justify-center text-xs font-bold text-white transition-all duration-200 group-hover:scale-105 group-hover:shadow-lg ${
                        activity.status === 'Completed' ? 'bg-gradient-to-br from-green-500 to-emerald-600 shadow-green-200' :
                        activity.status === 'In Progress' ? 'bg-gradient-to-br from-yellow-500 to-orange-500 shadow-yellow-200' :
                        'bg-gradient-to-br from-gray-400 to-gray-500 shadow-gray-200'
                      }`}>
                        {getInitials(activity.title)}
                      </div>
                      <div className="flex-1 min-w-0 pt-0.5">
                        <p className="text-sm font-semibold text-gray-900 truncate group-hover:text-blue-700 transition-colors">{activity.title}</p>
                        <p className="text-xs text-gray-500 mt-0.5 flex items-center space-x-1">
                          <span>{activity.category}</span>
                          <span className="text-gray-300">•</span>
                          <span>{activity.bagian || 'Umum'}</span>
                        </p>
                      </div>
                      <div className="flex flex-col items-end space-y-1 flex-shrink-0 pt-0.5">
                        <span className="text-[11px] text-gray-400 font-medium">{format(new Date(activity.date), 'MMM dd')}</span>
                        <span className={`inline-flex px-2 py-0.5 text-[10px] font-semibold rounded-full border ${getStatusColor(activity.status)}`}>
                          {activity.status}
                        </span>
                      </div>
                      <div className="flex items-center opacity-0 group-hover:opacity-100 transition-all duration-200 ml-1">
                        <div className="p-1 rounded-full bg-blue-50">
                          <ExternalLink className="w-3 h-3 text-blue-400" />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div className="text-center py-10">
                <div className="relative inline-flex mb-4">
                  <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-gradient-to-br from-gray-100 to-gray-200">
                    <TrendingUp className="w-7 h-7 text-gray-400" />
                  </div>
                  <div className="absolute -top-1 -right-1 w-4 h-4 rounded-full bg-gray-200 animate-ping opacity-60" />
                </div>
                <p className="text-gray-500 font-medium">Belum ada aktivitas terbaru</p>
                <p className="text-gray-400 text-sm mt-1">Tambahkan aktivitas pertama Anda</p>
              </div>
            )}
          </div>
        </div>

        {/* Recent Tickets */}
        <div className="card">
          <div className="card-header flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <Ticket className="w-5 h-5 text-indigo-500" />
              <h3 className="section-title">Recent Tickets</h3>
            </div>
            <div className="flex items-center space-x-2">
              <span className="bg-indigo-50 text-indigo-700 text-xs font-medium px-2.5 py-1 rounded-full">
                Latest {recentTickets.length}
              </span>
              <button
                onClick={() => goTo('tickets')}
                className="text-xs font-semibold text-indigo-600 hover:text-indigo-700 flex items-center space-x-1 transition-colors"
              >
                <span>View All</span>
                <ArrowRight className="w-3 h-3" />
              </button>
            </div>
          </div>
          <div className="card-body relative">
            {recentTickets.length > 0 ? (
              <div className="relative">
                {/* Timeline vertical line */}
                <div className="absolute left-5 top-2 bottom-2 w-0.5 bg-gradient-to-b from-indigo-200 via-indigo-100 to-transparent rounded-full" />
                <div className="space-y-1">
                  {recentTickets.map((ticket, i) => (
                    <div
                      key={ticket._id}
                      onClick={() => goTo('tickets')}
                      className="flex items-start space-x-3 p-3 rounded-lg hover:bg-indigo-50/70 transition-all duration-200 cursor-pointer group relative"
                      role="button"
                      tabIndex={0}
                      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); goTo('tickets'); } }}
                    >
                      {/* Timeline dot */}
                      <div className={`absolute left-[18px] top-[20px] w-1.5 h-1.5 rounded-full ring-2 ring-white z-10 transition-transform duration-200 group-hover:scale-150 ${
                        ticket.status === 'Open' ? 'bg-red-500' :
                        ticket.status === 'In Progress' ? 'bg-yellow-500' :
                        ticket.status === 'Resolved' ? 'bg-green-500' : 'bg-gray-400'
                      }`} />
                      <div className={`flex-shrink-0 w-9 h-9 rounded-xl flex items-center justify-center text-[11px] font-bold text-white transition-all duration-200 group-hover:scale-105 group-hover:shadow-lg ${
                        ticket.status === 'Open' ? 'bg-gradient-to-br from-red-500 to-rose-600 shadow-red-200' :
                        ticket.status === 'In Progress' ? 'bg-gradient-to-br from-yellow-500 to-orange-500 shadow-yellow-200' :
                        ticket.status === 'Resolved' ? 'bg-gradient-to-br from-green-500 to-emerald-600 shadow-green-200' :
                        'bg-gradient-to-br from-gray-400 to-gray-500 shadow-gray-200'
                      }`}>
                        {ticket.ticketNumber?.replace('TKT-', '') || '#'}
                      </div>
                      <div className="flex-1 min-w-0 pt-0.5">
                        <p className="text-sm font-semibold text-gray-900 truncate group-hover:text-indigo-700 transition-colors">{ticket.title}</p>
                        <p className="text-xs text-gray-500 mt-0.5 flex items-center space-x-1">
                          <span>{ticket.bagian}</span>
                          <span className="text-gray-300">•</span>
                          <span className={`font-medium ${
                            ticket.priority === 'High' ? 'text-red-600' :
                            ticket.priority === 'Medium' ? 'text-yellow-600' :
                            'text-green-600'
                          }`}>{ticket.priority}</span>
                        </p>
                      </div>
                      <span className={`inline-flex px-2 py-0.5 text-[10px] font-semibold rounded-full border self-center ${getTicketStatusColor(ticket.status)}`}>
                        {ticket.status}
                      </span>
                      <div className="flex items-center opacity-0 group-hover:opacity-100 transition-all duration-200 ml-1">
                        <div className="p-1 rounded-full bg-indigo-50">
                          <ExternalLink className="w-3 h-3 text-indigo-400" />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div className="text-center py-10">
                <div className="relative inline-flex mb-4">
                  <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-gradient-to-br from-indigo-100 to-indigo-200">
                    <Ticket className="w-7 h-7 text-indigo-400" />
                  </div>
                  <div className="absolute -top-1 -right-1 w-4 h-4 rounded-full bg-indigo-200 animate-ping opacity-60" />
                </div>
                <p className="text-gray-500 font-medium">Belum ada tiket terbaru</p>
                <p className="text-gray-400 text-sm mt-1">Buat tiket pertama Anda</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ─── Empty State ─────────────────────────────────── */}
      {activities.length === 0 && tickets?.length === 0 && (
        <div className="card p-12 text-center animate-fade-in">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gray-100 mb-4">
            <BarChart3 className="w-8 h-8 text-gray-400" />
          </div>
          <p className="text-gray-500 text-lg font-medium">Welcome to your Dashboard</p>
          <p className="text-gray-400 mt-1">Start by adding activities or submitting tickets</p>
        </div>
      )}
    </div>
  );
};

export default DashboardPage;
